/**
 * Sticker collection — /collection
 *
 * The child's persistent rewards showcase: every medal earned by
 * completing songs or mini-games accumulates here. No text: grid of
 * large icons; the gaps not yet earned show as dimmed medals
 * to invite them to keep playing.
 */

import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { createRoute, Link } from "@tanstack/solid-router";
import { ArrowLeft, Music, Star, Trophy } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import {
  createProgressStore,
  gameStickerId,
  SONGS,
  songStickerId,
  stickerIcon,
} from "@melodimals/game-core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { availableGameModules } from "@/games/registry";

/**
 * Obtainable stickers: one per mini-game OFFERABLE in this runtime and one per song.
 *
 * It is computed on mount, not as a module constant: if a module is not offered (Duo without
 * reachable signaling, see `signaling-availability.ts`), its sticker cannot be earned
 * and counting it would leave the collection capped at 90% forever.
 */
function allStickerIds(): string[] {
  return [
    ...availableGameModules()
      .filter((m) => m.manifest.kind === "game")
      .map((m) => gameStickerId(m.manifest.id)),
    ...SONGS.map((s) => songStickerId(s.id)),
  ];
}

/** Total learnable songs (for the music progress bar). */
const SONG_COUNT = SONGS.length;

function CollectionPage() {
  const { t } = useI18n();
  const [earned, setEarned] = createSignal<string[]>([]);
  const [songsLearned, setSongsLearned] = createSignal(0);

  onMount(() => {
    const progress = createProgressStore().load();
    setEarned(progress.stickers);
    setSongsLearned(progress.songsCompleted.length);
  });

  const has = (id: string) => earned().includes(id);

  // Global progress = stickers earned / obtainable total (0-100), rounded.
  const stickerIds = createMemo(allStickerIds);
  const totalStickers = createMemo(() => stickerIds().length);
  const earnedPct = createMemo(() =>
    totalStickers() === 0 ? 0 : Math.round((earned().length / totalStickers()) * 100),
  );

  return (
    <main class="kid-stage-screen screen-enter bg-background p-4">
      <header class="flex items-center gap-3 pb-3">
        <Link
          to="/"
          class="tile-3d tile-3d-press flex h-14 w-14 items-center justify-center rounded-full bg-surface"
          aria-label={t(msg`Back`)}
        >
          <ArrowLeft class="h-7 w-7 text-foreground" />
        </Link>
        <Trophy class="h-8 w-8 text-joy-tangerine" aria-hidden="true" />
        {/* Acceso a "Mi progreso": nivel/XP/racha + medallas de hitos. */}
        <Link
          to="/progress"
          data-testid="open-progress"
          class="tile-3d tile-3d-press ml-auto flex h-14 items-center gap-2 rounded-full bg-joy-grape/15 px-4 text-2xl"
          aria-label={t(msg`My level`)}
        >
          <span aria-hidden="true">🏅</span>
        </Link>
      </header>

      {/* Mi progreso: vista simple y visual (barras + medallas), sin librerías.
          Theme-aware vía tokens joy/surface; legible para padres y motivante
          para el niño (cuanto más juega, más se llena). */}
      <section
        data-testid="progress-summary"
        class="mb-4 space-y-3 rounded-3xl bg-surface p-4 shadow-sm"
      >
        <div class="flex items-center justify-between gap-3">
          <span class="flex items-center gap-2 text-sm font-semibold text-muted">
            <Star class="h-5 w-5 text-joy-tangerine" aria-hidden="true" />
            {t(msg`My stickers`)}
          </span>
          <span class="text-sm font-bold tabular-nums text-foreground">
            {earned().length} / {totalStickers()}
          </span>
        </div>
        <div
          class="h-4 w-full overflow-hidden rounded-full bg-surface-secondary"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={earnedPct()}
          aria-label={t(msg`Stickers collected`)}
        >
          {/* GPU-only fill: animate transform (scaleX), not width, so the bar
              never triggers layout. Origin-left makes it grow from the start. */}
          <div
            class="h-full w-full origin-left rounded-full bg-gradient-to-r from-joy-sunshine to-joy-tangerine transition-transform duration-500 ease-out"
            style={{ transform: `scaleX(${earnedPct() / 100})` }}
          />
        </div>
        <div class="flex items-center justify-between gap-3 pt-1">
          <span class="flex items-center gap-2 text-sm font-semibold text-muted">
            <Music class="h-5 w-5 text-joy-grape" aria-hidden="true" />
            {t(msg`Songs learned`)}
          </span>
          <span class="text-sm font-bold tabular-nums text-foreground">
            {songsLearned()} / {SONG_COUNT}
          </span>
        </div>
      </section>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div
          data-testid="sticker-collection"
          class="grid grid-cols-3 gap-4 pb-4 sm:grid-cols-4 lg:grid-cols-5"
          data-earned-count={earned().length}
        >
          <For each={stickerIds()}>
            {(id) => (
              <Show
                when={has(id)}
                fallback={
                  <div
                    class="flex aspect-square items-center justify-center rounded-full border-2 border-dashed border-muted/40 bg-surface-secondary text-5xl opacity-50 grayscale"
                    aria-hidden="true"
                  >
                    <span>{stickerIcon(id)}</span>
                  </div>
                }
              >
                <div
                  data-testid="earned-sticker"
                  data-sticker-id={id}
                  class="flex aspect-square items-center justify-center rounded-full bg-gradient-to-b from-joy-sunshine to-joy-tangerine text-5xl shadow-md ring-4 ring-white/70"
                >
                  <span aria-hidden="true">{stickerIcon(id)}</span>
                </div>
              </Show>
            )}
          </For>
        </div>
      </div>
    </main>
  );
}

export const collectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collection",
  component: CollectionPage,
});
